/**
 * The short-lived tiler credential every raster tile and meta request carries.
 *
 * The tiler accepts the token as a `token=` query parameter, which is what map libraries can
 * actually produce: MapLibre's `transformRequest` is synchronous and cannot set headers on
 * tile requests.
 */

/** What a forint viewer-token endpoint answers with, as far as this module cares. */
export interface ViewerCredential {
  token: string;
  expires_at: string;
  tiler_base_url: string;
}

export interface TokenSource {
  /** The current token, fetching or refreshing it if needed. */
  getToken(): Promise<string>;
  /** MapLibre's `transformRequest`: synchronous, so it can only use what is already cached. */
  transformRequest(url: string, resourceType?: string): { url: string } | undefined;
  /** Drops the cache so the next `getToken` mints a new one. */
  invalidate(): void;
  /** Counts the credentials that have arrived; a listener tells them apart by it. */
  readonly generation: number;
  /**
   * Called after every successful refresh, with the new generation. Tile URLs carry the token
   * through `transformRequest`, so nothing in a style changes when it does: whoever draws has
   * to refetch on its own. Returns the unsubscribe.
   */
  onRefresh(cb: (generation: number) => void): () => void;
  /** The tiler's origin, known once the first credential has arrived; `''` before that. */
  readonly baseUrl: string;
}

/** How long before expiry a refresh is started. The tokens live 15 minutes. */
export const REFRESH_MARGIN_MS = 60_000;

interface Cached {
  token: string;
  expiresAt: number;
  baseUrl: string;
}

export function createTokenSource(fetchToken: () => Promise<ViewerCredential>): TokenSource {
  let cached: Cached | null = null;
  let inFlight: Promise<Cached> | null = null;
  let generation = 0;
  const listeners = new Set<(generation: number) => void>();

  const stale = () => cached === null || Date.now() >= cached.expiresAt - REFRESH_MARGIN_MS;

  function refresh(): Promise<Cached> {
    inFlight ??= fetchToken()
      .then((c) => {
        cached = { token: c.token, expiresAt: Date.parse(c.expires_at), baseUrl: c.tiler_base_url };
        generation += 1;
        for (const cb of [...listeners]) cb(generation);
        return cached;
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  }

  return {
    async getToken() {
      if (stale()) return (await refresh()).token;
      return cached!.token;
    },

    transformRequest(url) {
      const current = cached;
      if (current === null || !url.startsWith(current.baseUrl)) return undefined;
      // Synchronous by contract: hand out the token we hold and let the refresh land in the
      // background, well before the current one stops being accepted.
      if (stale()) void refresh().catch(() => undefined);
      // Past the hard expiry the token is dead. Appending it buys nothing and only turns every
      // tile into a 401; let the request fail on its own and the refresh above bring it back.
      if (Date.now() >= current.expiresAt) return undefined;
      const sep = url.includes('?') ? '&' : '?';
      return { url: `${url}${sep}token=${encodeURIComponent(current.token)}` };
    },

    invalidate() {
      cached = null;
    },

    get generation() {
      return generation;
    },

    onRefresh(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },

    get baseUrl() {
      return cached?.baseUrl ?? '';
    },
  };
}
